import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography } from '@mui/material';

interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  fontSize?: number;
  minLines?: number;
  maxLines?: number;
  helperText?: string;
  label?: string;
  error?: boolean;
  fullWidth?: boolean;
}

// VS Code Dark+ Theme Colors
const VS_CODE_DARK = {
  background: '#1e1e1e',
  foreground: '#d4d4d4',
  lineNumbers: '#858585',
  selection: '#264f78',
  inactiveSelection: '#3a3d41',
  keyword: '#569cd6',
  string: '#ce9178',
  number: '#b5cea8',
  comment: '#6a9955',
  function: '#dcdcaa',
  variable: '#9cdcfe',
  operator: '#d4d4d4',
  tag: '#569cd6',
  attribute: '#9cdcfe',
  property: '#9cdcfe',
  className: '#4ec9b0',
  punctuation: '#d4d4d4',
  regex: '#d16969',
};

const CodeEditor: React.FC<CodeEditorProps> = ({
  value,
  onChange,
  readOnly = false,
  fontSize = 13,
  minLines = 15,
  maxLines = 30,
  helperText,
  label,
  error = false,
  fullWidth = true,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [lineCount, setLineCount] = useState(1);

  useEffect(() => {
    setLineCount(value.split('\n').length);
  }, [value]);

  // Simple syntax highlighting for JavaScript
  const highlightCode = (code: string) => {
    // Escape HTML entities first
    let escaped = code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Comments (// and /* */)
    escaped = escaped.replace(/(\/\/.*$)/gm, '<span style="color: #6a9955;">$1</span>');
    escaped = escaped.replace(/(\/\*[\s\S]*?\*\/)/g, '<span style="color: #6a9955;">$1</span>');

    // Strings (backticks, single, double quotes)
    escaped = escaped.replace(/(`[^`]*`)/g, '<span style="color: #ce9178;">$1</span>');
    escaped = escaped.replace(/('[^']*')/g, '<span style="color: #ce9178;">$1</span>');
    escaped = escaped.replace(/("[^"]*")/g, '<span style="color: #ce9178;">$1</span>');

    // Keywords
    const keywords = [
      'const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'do', 'switch',
      'case', 'break', 'continue', 'try', 'catch', 'finally', 'throw', 'async', 'await', 'new',
      'this', 'class', 'extends', 'import', 'export', 'default', 'from', 'of', 'in', 'typeof',
      'instanceof', 'null', 'undefined', 'true', 'false', 'delete', 'void'
    ];
    const keywordRegex = new RegExp(`\\b(${keywords.join('|')})\\b`, 'g');
    escaped = escaped.replace(keywordRegex, '<span style="color: #569cd6; font-weight: 500;">$1</span>');

    // Numbers
    escaped = escaped.replace(/\b(\d+\.?\d*)\b/g, '<span style="color: #b5cea8;">$1</span>');

    // Function calls
    escaped = escaped.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(/g, '<span style="color: #dcdcaa;">$1</span>(');

    // Properties after dot
    escaped = escaped.replace(/\.([a-zA-Z_$][a-zA-Z0-9_$]*)/g, '.<span style="color: #9cdcfe;">$1</span>');

    // Braces and brackets
    escaped = escaped.replace(/([{}[\]()])/g, '<span style="color: #ffd700;">$1</span>');

    // Template literals ${}
    escaped = escaped.replace(/\$\{([^}]+)\}/g, '<span style="color: #9cdcfe;">${</span><span style="color: #dcdcaa;">$1</span><span style="color: #9cdcfe;">}</span>');

    return escaped;
  };

  const highlightedCode = highlightCode(value);

  const handleScroll = () => {
    const highlightedDiv = document.getElementById('highlighted-code');
    const textarea = textareaRef.current;
    if (highlightedDiv && textarea) {
      highlightedDiv.scrollTop = textarea.scrollTop;
      highlightedDiv.scrollLeft = textarea.scrollLeft;
    }
  };

  const lineNumbers = Array.from({ length: lineCount }, (_, i) => i + 1);

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <Typography
          variant="body2"
          sx={{
            mb: 0.5,
            color: error ? '#f44336' : '#858585',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
      )}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: error ? '#f44336' : '#3c3c3c',
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: VS_CODE_DARK.background,
          '&:hover': !readOnly ? {
            borderColor: error ? '#f44336' : '#555',
          } : {},
          transition: 'border-color 0.2s',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            position: 'relative',
            minHeight: `${minLines * fontSize * 1.5}px`,
            maxHeight: `${maxLines * fontSize * 1.5}px`,
          }}
        >
          {/* Line Numbers */}
          <Box
            sx={{
              backgroundColor: '#1e1e1e',
              color: VS_CODE_DARK.lineNumbers,
              fontFamily: 'Consolas, "Courier New", monospace',
              fontSize: `${fontSize}px`,
              lineHeight: 1.5,
              py: 1,
              px: 1.5,
              textAlign: 'right',
              userSelect: 'none',
              borderRight: '1px solid #3c3c3c',
              minWidth: '45px',
              fontWeight: 400,
            }}
          >
            {lineNumbers.map((num) => (
              <Box key={num}>{num}</Box>
            ))}
          </Box>

          {/* Code Area */}
          <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
            {/* Highlighted Code Display */}
            <Box
              id="highlighted-code"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                p: 1,
                fontFamily: 'Consolas, "Courier New", monospace',
                fontSize: `${fontSize}px`,
                lineHeight: 1.5,
                color: VS_CODE_DARK.foreground,
                whiteSpace: 'pre',
                overflow: 'auto',
                pointerEvents: 'none',
              }}
            >
              <Box dangerouslySetInnerHTML={{ __html: highlightedCode + '\n' }} />
            </Box>

            {/* Editable Textarea */}
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              readOnly={readOnly}
              onScroll={handleScroll}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                padding: '4px',
                fontFamily: 'Consolas, "Courier New", monospace',
                fontSize: `${fontSize}px`,
                lineHeight: 1.5,
                color: 'transparent',
                backgroundColor: 'transparent',
                caretColor: '#d4d4d4',
                border: 'none',
                outline: 'none',
                resize: 'none',
                overflow: 'auto',
                whiteSpace: 'pre',
              }}
            />
          </Box>
        </Box>
      </Paper>
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            color: '#858585',
            display: 'block',
            fontSize: '0.75rem',
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default CodeEditor;
